import * as Sentry from '@sentry/react'

// Initialize Sentry only if DSN is provided
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const isProd = import.meta.env.PROD

if (sentryDsn) {
  // On event day ~200 players will hit this app on mobile data. Session Replay
  // and console-mirroring are great in staging but expensive over LTE and
  // noisy in production. We keep error-only replays (which are what we
  // actually need when something breaks on-site) and drop the console
  // integration in prod.
  const integrations = [Sentry.browserTracingIntegration()]

  if (!isProd) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    )
  } else {
    // Production: error-triggered replays only. Text stays unmasked so we can
    // actually debug score-entry flows, but masking media saves bandwidth.
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),
    )
  }

  Sentry.init({
    dsn: sentryDsn,
    integrations,

    // Performance Monitoring — drop to 5% on event day to keep mobile payload small.
    tracesSampleRate: isProd ? 0.05 : 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/puttzin\.com/,
      /^https:\/\/.*\.vercel\.app/,
      /^https:\/\/.*\.supabase\.co/,
    ],

    // Session Replay
    // Prod: never record unless there's an error. Preview/dev: sample 10%.
    replaysSessionSampleRate: isProd ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Console mirroring only outside prod — avoids shipping every console.log
    // over the wire for 200 concurrent players.
    enableLogs: !isProd,
    
    // Avoid collecting default PII for an event app where team names/PIN flows
    // are enough context for debugging without retaining IP-derived identity.
    sendDefaultPii: false,
    
    environment: import.meta.env.VITE_VERCEL_ENV || import.meta.env.MODE || 'development',
    
    // Stale Vite chunk (race before chunkLoadRecovery reload) — drop to reduce alert noise
    beforeSend(event, _hint) {
      const v0 = event?.exception?.values?.[0]
      const msg = [v0?.value, v0?.type, event?.message]
        .filter(Boolean)
        .join(' ')
      if (/not a valid JavaScript MIME type|Failed to fetch dynamically imported module/i.test(msg)) {
        return null
      }
      return event
    },

    // Filter out common non-critical errors
    ignoreErrors: [
      // Stale / missing Vite chunk after new deploy (handled by chunkLoadRecovery)
      'not a valid JavaScript MIME type',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // Supabase connection issues (handled by app)
      'WebSocket connection',
    ],
    
    // Filter out URLs that shouldn't be tracked
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  })
  
  console.log('✅ Sentry error tracking initialized')
} else {
  console.log('⚠️ Sentry DSN not provided - error tracking disabled')
}

export default Sentry
